/**
 * eventText — decoding the text that comes off the live events feed.
 *
 * WHY THIS EXISTS. The live calendar is scraped HTML at the far end of the
 * pipe, so its titles arrive with HTML entities still in them, and every layer
 * between there and the screen treats a title as opaque text. React then escapes
 * on render — correctly — so `&amp;` is drawn as the four characters it is:
 *
 *     PokémonXP &amp; 2026 Worlds
 *
 * Reported on device 2026-08-12. It is not a rendering bug and there is no
 * `dangerouslySetInnerHTML` fix for it: the string is simply wrong by the time
 * it reaches the client, and the fix is to decode ONCE, on the way in.
 *
 * ENTITIES ONLY — never markup. This decodes character references and nothing
 * else; a title that somehow contained a tag stays inert text, so no feed can
 * ever put markup into the page through this door.
 *
 * The list is deliberately small: `&amp;` is the one that actually shows up
 * (ampersands are everywhere in event names — "Regirock, Regice & Registeel"),
 * with the other four ASCII entities and numeric references for completeness.
 * `&amp;` is unescaped LAST so a double-encoded `&amp;amp;` resolves to `&amp;`
 * rather than collapsing a step too far.
 */

const NAMED: Record<string, string> = {
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&#x27;": "'",
  "&nbsp;": " ",
  // Bonus lines multiply ("2× Catch Candy"), so the multiplication sign shows
  // up entity-encoded in scraped bonus text the way &amp; does in titles.
  "&times;": "×",
};

/**
 * Decode HTML character references in a scraped string, and tidy the whitespace
 * that decoding a `&nbsp;` can leave behind.
 *
 * A string with no entity in it comes back untouched, spacing and all — the
 * tidy-up is part of decoding, not a formatter applied to every title.
 *
 * Safe to run twice on the same string in every case except a deliberately
 * double-encoded one, which is the point of the ordering above. That matters:
 * both the worker and the client decode, because a cached response outlives a
 * deploy.
 */
export function decodeEventText(input: string): string {
  if (!input || input.indexOf("&") === -1) return input;
  let out = input;
  for (const [entity, char] of Object.entries(NAMED)) {
    out = out.split(entity).join(char);
  }
  // Numeric references: &#8217; and &#x2019; alike.
  out = out.replace(/&#(x[0-9a-f]+|\d+);/gi, (whole, code: string) => {
    const point = code[0] === "x" || code[0] === "X"
      ? Number.parseInt(code.slice(1), 16)
      : Number.parseInt(code, 10);
    if (!Number.isFinite(point) || point <= 0 || point > 0x10ffff) return whole;
    try {
      return String.fromCodePoint(point);
    } catch {
      return whole;
    }
  });
  // Last, so `&amp;lt;` has already become `&lt;`… and stays text.
  out = out.split("&amp;").join("&");
  return out.replace(/\s+/g, " ").trim();
}

/** Decode when there is something to decode; pass `undefined` straight through. */
export function decodeEventTextOptional(input: string | null | undefined): string | undefined {
  if (typeof input !== "string" || input.length === 0) return undefined;
  return decodeEventText(input);
}
